import React, { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { WASTE_CATEGORIES, MASCOTS, fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { JournalPreviewButton } from "../components/JournalPreview";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { ProductSelect } from "../components/ProductSelect";

export default function Waste() {
  const { isRole } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const [waste, setWaste] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ product_id: "", quantity: "", category: "EXPIRED", reason: "", notes: "", photo: null });

  const load = () => api.get("/waste").then(({ data }) => setWaste(data));
  useEffect(() => {
    load();
    api.get("/products?active_only=true").then(({ data }) => setProducts(data));
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);
  if (!waste) return <PageLoader />;

  const readPhoto = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, photo: reader.result }));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.product_id || !form.quantity) return toast.error(t("Select product and quantity"));
    if (!form.reason.trim()) return toast.error(t("A reason is required for every waste report"));
    if (!form.photo) return toast.error(t("A photo is required as waste evidence"));
    setBusy(true);
    try {
      const { data } = await api.post("/waste", { ...form, quantity: Number(form.quantity) });
      if (data.pending_approval) toast.success(t("Waste submitted — pending HQ approval (over threshold)"));
      else toast.success(t("Waste reported & stock reduced"));
      setOpen(false); setForm({ product_id: "", quantity: "", category: "EXPIRED", reason: "", notes: "", photo: null }); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const totalValue = waste.reduce((s, w) => s + (w.waste_value || 0), 0);
  const canAdd = !isRole("OWNER");

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Waste Management")} subtitle={t("Track and value wasted stock")} icon={Trash2}>
        {canAdd && <Button onClick={() => setOpen(true)} data-testid="report-waste-button"><Plus size={18} /> {t("Report Waste")}</Button>}
      </PageHeader>

      <Card className="mb-5 flex items-center justify-between p-5">
        <div>
          <p className="text-sm text-ink-soft">{t("Total waste value recorded")}</p>
          <p className="font-heading text-2xl font-bold text-danger-text">{fmtRp(totalValue)}</p>
        </div>
        <img src={MASCOTS.waste} alt="" className="h-16 w-16 object-contain" />
      </Card>

      {waste.length === 0 ? (
        <EmptyState image={MASCOTS.waste} title={t("No waste recorded")} subtitle={t("Great job keeping waste low! Report any waste here to keep stock accurate.")} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {waste.map((w) => {
            const p = products.find((x) => x.id === w.product_id) || {};
            return (
            <Card key={w.id} className="p-4 transition-all hover:shadow-cardhover" data-testid={`waste-card`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <ProductAvatar image={p.image} colorKey={p.category_color} name={w.product_name} size="sm" />
                  <div>
                    <p className="font-heading font-bold leading-tight">{w.product_name}</p>
                    <p className="text-xs text-ink-mute"><span className="font-bold text-ink-soft">{fmtNum(w.quantity)} {fmtUnit(w.unit)}</span> · {t("by")} {w.created_by_name}</p>
                  </div>
                </div>
                <Badge tone="danger">{t(WASTE_CATEGORIES.find((c) => c.value === w.category)?.label || w.category)}</Badge>
              </div>
              {w.reason && <p className="mt-2 text-sm text-ink-soft">{w.reason}</p>}
              <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
                <span className="text-sm font-bold text-danger-text">{fmtRp(w.waste_value)}</span>
                <div className="flex items-center gap-2">
                  {w.status === "APPROVED" && <JournalPreviewButton module="waste" docId={w.id} />}
                  {w.status && <Badge tone={w.status === "APPROVED" ? "success" : w.status === "PENDING" ? "warn" : "mute"} data-testid={`waste-status-${w.status}`}>{w.status === "PENDING" ? t("Pending Approval") : t(w.status)}</Badge>}
                  {w.photo && <img src={w.photo} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={t("Report Waste")}
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><Button variant="danger" onClick={submit} loading={busy} data-testid="submit-waste">{t("Report")}</Button></>}>
        <div className="mb-3">
          <label className="cf-label">{t("Product")}</label>
          <ProductSelect products={products} categories={categories} value={form.product_id}
            onChange={(pid) => setForm({ ...form, product_id: pid })}
            placeholder={t("Select product...")} testid="waste-product" />
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div><label className="cf-label">{t("Quantity")}</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="cf-input" data-testid="waste-qty" /></div>
          <Select label={t("Category")} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="waste-category">
            {WASTE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{t(c.label)}</option>)}
          </Select>
        </div>
        <div className="mb-3"><label className="cf-label">{t("Reason")} <span className="text-danger">*</span></label><input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="cf-input" placeholder={t("Why is this being wasted?")} data-testid="waste-reason" /></div>
        <div className="mb-3"><label className="cf-label">{t("Notes")}</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="cf-input" /></div>
        <div className="mb-3"><label className="cf-label">{t("Photo Evidence")} <span className="text-danger">*</span></label><input type="file" accept="image/*" onChange={readPhoto} className="cf-input" data-testid="waste-photo" />
          {form.photo && <img src={form.photo} alt="" className="mt-2 h-20 rounded-lg object-cover" />}</div>
        <p className="rounded-lg bg-warn-light px-3 py-2 text-xs text-warn-text">{t("Waste over")} <b>Rp 100,000</b> {t("or more than")} <b>10%</b> {t("of current branch stock needs HQ approval before stock is reduced.")}</p>
      </Modal>
    </div>
  );
}
